













import type { PropType } from '@nuxtjs/composition-api';
import {
  computed,
  defineComponent,
  onMounted,
  ref
} from '@nuxtjs/composition-api';
import { SfBreadcrumbs } from '@storefront-ui/vue';
import { useEventListener, useThrottleFn } from '@vueuse/core';

import { useLink } from '~/diptyqueTheme/composable/useLink';
import type { Breadcrumb } from '~/modules/GraphQL/types';

export default defineComponent({
  name: 'VaimoBreadcrumbs',
  components: {
    SfBreadcrumbs
  },
  props: {
    breadcrumbs: {
      type: Array as PropType<Breadcrumb[]>,
      default: () => []
    },
    design: {
      type: String as PropType<'latest' | 'default'>,
      required: false,
      default: 'default'
    }
  },
  setup(props) {
    const { normalizeLink } = useLink();
    const breadcrumbsWrapper = ref(null);
    const scrollPosition = ref(0);
    const contentScrollWidth = ref(0);
    const RIGHT_FADE_THRESHOLD = 0.95;
    const SCROLL_THROTTLE_DELAY = 100;

    const preprocessedBreadcrumbs = computed<Breadcrumb[]>(() =>
      props.breadcrumbs.map((b) => {
        const link = normalizeLink(b.link) || '';
        const text = b.title && !b.text ? b.title : b.text;
        return {
          ...b,
          link,
          text
        };
      })
    );

    const showLeftFade = computed<boolean>(() => scrollPosition.value > 0);
    const showRightFade = computed<boolean>(
      () =>
        scrollPosition.value < RIGHT_FADE_THRESHOLD &&
        breadcrumbsWrapper.value?.scrollWidth !==
          breadcrumbsWrapper.value?.offsetWidth
    );

    const wrapperClasses = computed<Record<string, boolean>>(() => {
      return {
        'breadcrumbs-fade-left': showLeftFade.value,
        'breadcrumbs-fade-right': showRightFade.value
      };
    });

    const handleScroll = () => {
      const wrapper = breadcrumbsWrapper.value;
      contentScrollWidth.value = wrapper.scrollWidth - wrapper.offsetWidth;
      scrollPosition.value = wrapper.scrollLeft / contentScrollWidth.value;
    };

    const throttledHandleScroll = useThrottleFn(
      handleScroll,
      SCROLL_THROTTLE_DELAY
    );

    onMounted(() => {
      useEventListener(breadcrumbsWrapper, 'scroll', throttledHandleScroll);
    });

    return {
      preprocessedBreadcrumbs,
      breadcrumbsWrapper,
      wrapperClasses
    };
  }
});
